import type { ChallengeName } from 'amazon-cognito-identity-js';
import { makeAutoObservable, observable } from 'mobx';

import type { IMfaParams } from '../../auth/Login/index';

export type LoginChallenge = {
  name: ChallengeName;
  params?: IMfaParams;
  onFinish(value: string): void;
  onResend?(): void;
  onLogin?(): void;
  onBack?(): void;
};

export enum forgotPasswordState {
  confirmed,
  identification,
}

export type LoginWithSsoState = {
  loginUrls: { provider: string; url: string }[];
};

export interface IForgotPassword {
  value?: boolean;
  state?: forgotPasswordState;
}

export interface ILoginWithSso {
  value?: boolean;
  state?: LoginWithSsoState;
  isRequiredByOrganization?: boolean;
}

export interface ILoginStore {
  cognitoUsername: string;
  setCognitoUsername(value: string): void;
  errorMessage: string;
  setErrorMessager(error: string): void;
  isLoading: boolean;
  setIsLoading(loading: boolean): void;
  isWelcome: boolean;
  setIsWelcome(value: boolean): void;
  idToken: string;
  setIdToken(value: string): void;
  challenge: LoginChallenge;
  setChallenge(challenge: LoginChallenge): void;
  profileStepOnComplete: (() => void) | null;
  setProfileStepOnComplete(callback: (() => void) | null): void;
  isResolvingLogin: boolean;
  setIsResolvingLogin(value: boolean): void;
  forgotPassword: IForgotPassword;
  setForgotPassword(forgotPasswordValue: IForgotPassword): void;
  resetLoginStates(): void;
  resetChallenge(): void;
  isLoginingWithOtp: boolean;
  setIsLoginingWithOtp(loading: boolean): void;
  loginWithSso: ILoginWithSso;
  setLoginWithSso(value: ILoginWithSso): void;
}

export class LoginStore implements ILoginStore {
  errorMessage = '';
  isLoading = false;
  isWelcome: boolean = false;
  challenge = {} as LoginChallenge;
  profileStepOnComplete: (() => void) | null = null;
  isResolvingLogin = false;
  forgotPassword = {} as IForgotPassword;
  cognitoUsername = '';
  idToken = '';
  isLoginingWithOtp = false;
  loginWithSso = {} as ILoginWithSso;

  constructor() {
    makeAutoObservable(this, { profileStepOnComplete: observable.ref });
  }

  setCognitoUsername(value: string): void {
    this.cognitoUsername = value;
  }

  setIsLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setErrorMessager(error: string) {
    this.errorMessage = error;
  }

  setChallenge(loginChallenge: LoginChallenge) {
    this.challenge = loginChallenge;
    this.errorMessage = '';
  }

  setProfileStepOnComplete(callback: (() => void) | null) {
    this.profileStepOnComplete = callback;
  }

  setIsResolvingLogin(value: boolean) {
    this.isResolvingLogin = value;
  }

  setForgotPassword(forgotPasswordValue: IForgotPassword) {
    this.forgotPassword = forgotPasswordValue;
    this.errorMessage = '';
  }

  setLoginWithSso(value: ILoginWithSso) {
    this.loginWithSso = value;
  }

  resetLoginStates() {
    this.forgotPassword = {} as IForgotPassword;
    this.errorMessage = '';
    this.isLoading = false;
    this.isWelcome = false;
    this.idToken = '';
    this.challenge = {} as LoginChallenge;
    this.profileStepOnComplete = null;
    this.isResolvingLogin = false;
    this.isLoginingWithOtp = false;
    this.loginWithSso = {} as ILoginWithSso;
  }

  resetChallenge() {
    this.challenge = {} as LoginChallenge;
  }

  setIsWelcome(value: boolean): void {
    this.isWelcome = value;
  }

  setIdToken(value: string): void {
    this.idToken = value;
  }

  setIsLoginingWithOtp(loading: boolean) {
    this.isLoginingWithOtp = loading;
  }
}
